/**
 * Страница просмотра интерактивной гиперболической карты.
 *
 * `MapPage` собирает воедино строительные блоки подсистемы карт и загружает
 * данные карты через публичный эндпоинт `GET /api/maps/:id` (Требование 10.2):
 * - {@link WebGLMapRenderer} — отрисовка меша LOD, границ диска и фундаментальной
 *   области с фоновым цветом карты (Требование 4.6, 4.7);
 * - {@link MapViewController} — навигация (перетаскивание/зум) как изометрия вида
 *   `V` с непрерывностью поверхности через склейку (Требования 4.1–4.5);
 * - {@link LodManager} — подбор уровня детализации и отсечение треугольников по
 *   видимой области (Требование 3.x);
 * - {@link DrawingLayer} — отрисовка сохранённых примитивов рисования в
 *   координатах поверхности (Требование 10.2, 10.3);
 * - {@link MarkerLayer} — иконки меток и hit-test; клик открывает боковую панель
 *   {@link LegendPanel} с развёрнутым описанием элемента легенды (Требование 5).
 *
 * Цикл отрисовки ({@link MapPage.render}): на изменение вида/масштаба
 * `LodManager.update → renderer.setTriangleMesh → renderer.render` (фон, меш,
 * граница диска), затем `DrawingLayer.render` (рисунки),
 * `renderer.renderDebugOverlay` (отладочные наложения поверх слоя рисования,
 * Требования 10.3, 10.4) и `MarkerLayer.update` (метки).
 *
 * Без авторизации карта доступна только для просмотра (Требование 7.4). При
 * наличии действующей админ-сессии ({@link MapPage.checkAdminSession}/
 * {@link MapPage.login}) включается режим редактирования
 * ({@link MapPage.enableEditing}): панель инструментов рисования
 * ({@link DrawingTools}), размещение меток ({@link MarkerEditor}) и сохранение
 * рисунков/меток через `PUT` эндпоинты `/api/mapsadmin` с заголовком авторизации
 * (Требования 7.1, 7.5, 10.1, 10.4). {@link MapPage.logout} очищает учётные
 * данные и скрывает инструменты (Требование 7.3). Проверка сессии выполняется
 * так же, как в `admin.html` — пробным запросом к защищённому эндпоинту
 * (см. {@link AdminSession}).
 *
 * Логика разбора и валидации ответа сервера вынесена в чистую функцию
 * {@link parseMapResponse} (без DOM/WebGL), покрываемую юнит-тестами; сборка
 * DOM/WebGL проверяется вручную по конвенции проекта.
 *
 * Требования: 4, 5, 10.2, 7.4
 */

import type {
  DrawingPrimitive,
  FundamentalPolygon,
  LegendItem,
  MapConfig,
  MapMarker,
  SidePairingSpec,
} from '@langton/shared';
import {
  computeSurfacePairing,
  createGeometry,
  type Geometry,
  type SurfacePairing,
} from '@langton/shared';
import type { Complex } from '@langton/shared';
import {
  WebGLMapRenderer,
  renderWebGLUnavailableMessage,
} from './webgl-map-renderer.js';
import { MapViewController, type Viewport } from './map-view-controller.js';
import { LodManager } from './lod-manager.js';
import { DrawingLayer, DrawingHistory } from './drawing-layer.js';
import { MarkerLayer } from './marker-layer.js';
import { LegendPanel } from './legend-panel.js';
import { DrawingTools } from './drawing-tools.js';
import { MarkerEditor } from './marker-editor.js';
import { AdminSession, type CredentialStorage } from './map-auth.js';

/**
 * Разобранные данные карты: конфигурация плюс легенда, метки и рисунки.
 * Соответствует телу ответа `GET /api/maps/:id`.
 */
export interface MapData {
  config: MapConfig;
  legend: LegendItem[];
  markers: MapMarker[];
  drawings: DrawingPrimitive[];
}

/** Режим взаимодействия в режиме редактирования (Требования 7.1, 8, 9). */
export type EditingMode = 'navigate' | 'draw' | 'markers';

/** Статус сохранения контента карты (для индикации в UI). */
export type SaveStatus = 'saving' | 'saved' | 'error';

/** Опции конфигурации {@link MapPage}. */
export interface MapPageOptions {
  /**
   * Базовый URL API (без завершающего слэша). По умолчанию пустая строка —
   * запросы идут на тот же origin (`/api/maps/:id`).
   */
  apiBase?: string;
  /**
   * Функция загрузки (внедрение зависимости для тестов). По умолчанию —
   * глобальный `fetch`.
   */
  fetchFn?: typeof fetch;
  /** Сторона появления боковой панели легенды (по умолчанию `'right'`). */
  legendPanelSide?: 'left' | 'right';
  /**
   * Хранилище учётных данных админ-сессии (Требование 7.1). По умолчанию —
   * `localStorage`. Передайте `null`, чтобы держать сессию только в памяти.
   */
  authStorage?: CredentialStorage | null;
  /** Ключ в хранилище для заголовка Basic-авторизации карт-админки. */
  authStorageKey?: string;
  /**
   * Колбэк изменения состояния авторизации (Требования 7.2, 7.3, 7.4): вызывается
   * после проверки сессии/входа/выхода. Страница карты (`map.html`) использует его
   * для показа/скрытия кнопки выхода и модалки входа.
   */
  onAuthChange?: (authenticated: boolean) => void;
  /** Колбэк статуса сохранения рисунков/меток (Требование 10.1). */
  onSaveStatus?: (status: SaveStatus) => void;
}

/** Значение по умолчанию для scale карты, если оно некорректно в конфигурации. */
const DEFAULT_MAP_SCALE = 4;

/**
 * Проверить, что значение — конечное число.
 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Проверить, что значение похоже на точку {@link Complex} с конечными `re`/`im`.
 */
function isComplexLike(value: unknown): value is { re: number; im: number } {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const p = value as { re?: unknown; im?: unknown };
  return isFiniteNumber(p.re) && isFiniteNumber(p.im);
}

/**
 * Разобрать и провалидировать тело ответа `GET /api/maps/:id`.
 *
 * Чистая функция без DOM/WebGL — единый источник истины для формирования данных
 * карты; покрывается юнит-тестами. Возвращает нормализованную структуру
 * {@link MapData}; при отсутствии обязательных полей бросает `Error` с понятным
 * сообщением. Необязательные коллекции (легенда, метки, рисунки) при отсутствии
 * приводятся к пустым массивам; некорректные элементы отбрасываются.
 *
 * @param json разобранный JSON ответа сервера
 * @returns нормализованные данные карты
 * @throws Error если структура или обязательные поля конфигурации некорректны
 */
export function parseMapResponse(json: unknown): MapData {
  if (!json || typeof json !== 'object') {
    throw new Error('Некорректный ответ карты: ожидался объект');
  }
  const body = json as Record<string, unknown>;

  const rawConfig = body.config;
  if (!rawConfig || typeof rawConfig !== 'object') {
    throw new Error('Некорректный ответ карты: отсутствует конфигурация (config)');
  }
  const cfg = rawConfig as Record<string, unknown>;

  if (typeof cfg.id !== 'string' || cfg.id === '') {
    throw new Error('Некорректная конфигурация карты: отсутствует идентификатор');
  }

  // Фундаментальный многоугольник: обязателен, нужен как минимум пара вершин.
  const rawPolygon = cfg.polygon;
  if (!rawPolygon || typeof rawPolygon !== 'object') {
    throw new Error('Некорректная конфигурация карты: отсутствует многоугольник (polygon)');
  }
  const polygonObj = rawPolygon as Record<string, unknown>;
  const vertices = polygonObj.vertices;
  if (!Array.isArray(vertices) || vertices.length < 2 || !vertices.every(isComplexLike)) {
    throw new Error(
      'Некорректная конфигурация карты: многоугольник не содержит корректных вершин',
    );
  }
  const polygon: FundamentalPolygon = {
    sides: isFiniteNumber(polygonObj.sides) ? polygonObj.sides : vertices.length,
    preset: typeof polygonObj.preset === 'string' ? polygonObj.preset : undefined,
    vertices: vertices.map((v) => ({ re: (v as { re: number }).re, im: (v as { im: number }).im })),
    angleSum: isFiniteNumber(polygonObj.angleSum) ? polygonObj.angleSum : 0,
  };

  // Схема склейки: обязательно слово склейки.
  const rawPairing = cfg.sidePairing;
  if (!rawPairing || typeof rawPairing !== 'object') {
    throw new Error('Некорректная конфигурация карты: отсутствует схема склейки (sidePairing)');
  }
  const pairingObj = rawPairing as Record<string, unknown>;
  if (typeof pairingObj.word !== 'string' || pairingObj.word === '') {
    throw new Error('Некорректная конфигурация карты: пустая схема склейки');
  }
  const sidePairing: SidePairingSpec = {
    word: pairingObj.word,
    generators: Array.isArray(pairingObj.generators)
      ? (pairingObj.generators as SidePairingSpec['generators'])
      : undefined,
  };

  const scale = isFiniteNumber(cfg.scale) && cfg.scale > 0 ? cfg.scale : DEFAULT_MAP_SCALE;

  const config: MapConfig = {
    id: cfg.id,
    name: typeof cfg.name === 'string' ? cfg.name : 'Карта',
    geometryType:
      cfg.geometryType === 'euclidean' || cfg.geometryType === 'spherical'
        ? cfg.geometryType
        : 'hyperbolic',
    polygon,
    sidePairing,
    scale,
    backgroundColor:
      typeof cfg.backgroundColor === 'string' ? cfg.backgroundColor : '#000000',
    visible: cfg.visible !== false,
    // Множитель максимального отдаления (Требование 2). Обязательное поле
    // конфигурации: читается из ответа сервера, при отсутствии/некорректности —
    // фолбэк `1` (видна ровно одна фундаментальная область, Требование 2.5).
    // Значение доходит до контроллера вида (предел `minScale`) и через него — до
    // LOD-менеджера (Требования 2.3, 2.4). Образец — `map-preview.ts`.
    maxZoomOut: isFiniteNumber(cfg.maxZoomOut) && cfg.maxZoomOut > 0 ? cfg.maxZoomOut : 1,
    // Админский множитель максимального отдаления (Требование 11). Читается из
    // ответа сервера, при отсутствии/некорректности — фолбэк `1` (совпадает со
    // значением по умолчанию `maxZoomOut`, обратная совместимость, Требование 11.6).
    adminMaxZoomOut:
      isFiniteNumber(cfg.adminMaxZoomOut) && cfg.adminMaxZoomOut > 0 ? cfg.adminMaxZoomOut : 1,
    // Глубина прорисовки копий (Требование 12). Читается из ответа сервера, при
    // отсутствии/некорректности — фолбэк `8` (текущий neighborDepth менеджера
    // LOD, обратная совместимость, Требование 12.6). Значение доходит до
    // LOD-менеджера как `neighborDepth` (Требования 12.1, 12.2).
    copyDepth:
      isFiniteNumber(cfg.copyDepth) && Number.isInteger(cfg.copyDepth) && cfg.copyDepth >= 1
        ? cfg.copyDepth
        : 8,
    // Обход копий «вершинным кольцом» вместо BFS по длине слова. Фолбэк `false`
    // (историческое поведение) для записей без поля/столбца.
    vertexRingTraversal: cfg.vertexRingTraversal === true,
    // Скрыть границу окружности Пуанкаре. Фолбэк `false` (граница видима) для
    // записей без поля/столбца.
    hideDiskBoundary: cfg.hideDiskBoundary === true,
    // Ссылка на превью-изображение (Требование 9). Пустая строка/отсутствие
    // трактуются как «не задано» (фолбэк на статичный рендер в галерее).
    previewImageUrl:
      typeof cfg.previewImageUrl === 'string' && cfg.previewImageUrl.trim() !== ''
        ? cfg.previewImageUrl
        : undefined,
    createdAt: typeof cfg.createdAt === 'string' ? cfg.createdAt : undefined,
    updatedAt: typeof cfg.updatedAt === 'string' ? cfg.updatedAt : undefined,
  };

  return {
    config,
    legend: parseLegend(body.legend),
    markers: parseMarkers(body.markers, config.id),
    drawings: parseDrawings(body.drawings),
  };
}

/** Разобрать список элементов легенды, отбрасывая некорректные записи. */
function parseLegend(raw: unknown): LegendItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const items: LegendItem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.id !== 'string' || typeof e.name !== 'string') {
      continue;
    }
    items.push({
      id: e.id,
      mapId: typeof e.mapId === 'string' ? e.mapId : '',
      iconUrl: typeof e.iconUrl === 'string' ? e.iconUrl : undefined,
      name: e.name,
      shortDescription:
        typeof e.shortDescription === 'string' ? e.shortDescription : undefined,
      richDescription:
        typeof e.richDescription === 'string' ? e.richDescription : undefined,
      sortOrder: isFiniteNumber(e.sortOrder) ? e.sortOrder : 0,
    });
  }
  return items;
}

/** Разобрать список меток, отбрасывая записи без корректных координат поверхности. */
function parseMarkers(raw: unknown, mapId: string): MapMarker[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const markers: MapMarker[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.id !== 'string' || typeof e.legendItemId !== 'string') {
      continue;
    }
    if (!isComplexLike(e.surface)) {
      continue;
    }
    const surface = e.surface as { re: number; im: number };
    markers.push({
      id: e.id,
      mapId: typeof e.mapId === 'string' ? e.mapId : mapId,
      legendItemId: e.legendItemId,
      surface: { re: surface.re, im: surface.im },
    });
  }
  return markers;
}

/** Разобрать список примитивов рисования (валидация делегируется DrawingLayer). */
function parseDrawings(raw: unknown): DrawingPrimitive[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw as DrawingPrimitive[];
}

/**
 * Страница просмотра карты: собирает рендерер, навигацию, слои и легенду,
 * загружает данные карты и запускает цикл отрисовки.
 *
 * Жизненный цикл:
 * 1. `new MapPage(container, mapId, options?)` — создаёт холст, оверлей меток и
 *    боковую панель; при отсутствии WebGL показывает сообщение.
 * 2. `await load()` — загружает данные карты и собирает подсистемы.
 * 3. внутренний цикл `render()` — перерисовка при изменении вида/масштаба/размера.
 * 4. `dispose()` — освобождение ресурсов.
 */
export class MapPage {
  private readonly container: HTMLElement;
  private readonly mapId: string;
  private readonly options: Required<
    Omit<
      MapPageOptions,
      'fetchFn' | 'authStorage' | 'authStorageKey' | 'onAuthChange' | 'onSaveStatus'
    >
  > & {
    fetchFn: typeof fetch;
    onAuthChange?: (authenticated: boolean) => void;
    onSaveStatus?: (status: SaveStatus) => void;
  };

  /** Админ-сессия: проверка авторизации, вход/выход, заголовки сохранения. */
  private readonly session: AdminSession;

  /** Доступен ли WebGL (проверяется в конструкторе). */
  private readonly webglAvailable: boolean;

  // DOM-структура (создаётся в конструкторе, если WebGL доступен).
  private canvas: HTMLCanvasElement | null = null;
  private overlay: HTMLElement | null = null;

  // Подсистемы карты (создаются в load()).
  protected renderer: WebGLMapRenderer | null = null;
  protected controller: MapViewController | null = null;
  protected lod: LodManager | null = null;
  /** Реализация геометрии карты (Требования 6, 8); задаётся в {@link assemble}. */
  protected geometry: Geometry | null = null;
  protected drawingLayer: DrawingLayer | null = null;
  protected markerLayer: MarkerLayer | null = null;
  protected legendPanel: LegendPanel | null = null;

  // Данные загруженной карты (для повторного использования подклассами/задачей 20.2).
  protected data: MapData | null = null;

  /**
   * Флаг режима редактирования. По умолчанию `false` (только просмотр,
   * Требование 7.4); включается после проверки админ-сессии (Требование 7.1).
   */
  protected editing = false;

  // Подсистемы режима редактирования (создаются в enableEditing()).
  protected drawingTools: DrawingTools | null = null;
  protected markerEditor: MarkerEditor | null = null;

  /**
   * История снимков слоя рисования для отмены действий (Ctrl+Z, Требование 10).
   * Создаётся вместе с режимом редактирования; хранит состояние слоя до каждого
   * завершённого действия (Требования 10.1, 10.3, 10.4).
   */
  protected drawingHistory: DrawingHistory | null = null;
  /**
   * Число меток на момент последнего изменения — служит для распознавания
   * именно установки метки (число растёт) и исключает засорение истории
   * перемещением/удалением (Требование 10.1).
   */
  private lastMarkerCount = 0;

  /** Текущий режим взаимодействия в режиме редактирования. */
  private editingMode: EditingMode = 'navigate';
  /** Есть ли несохранённые изменения рисунков/меток (Требование 10.1). */
  private dirty = false;

  /**
   * Отладочное отображение границ фундаментального многоугольника
   * (Требование 3). Чисто клиентское UI-состояние: не сохраняется на сервер и
   * инициализируется выключенным (Требование 3.4). Границы рисуются только когда
   * авторизованный администратор включил этот режим тумблером (Требования 3.2,
   * 3.5, 3.7).
   */
  protected debugBorders = false;

  // DOM панели инструментов редактирования и её управляющие элементы.
  private editingPanel: HTMLElement | null = null;
  private modeButtons = new Map<EditingMode, HTMLElement>();
  private legendSelect: HTMLSelectElement | null = null;
  private saveButton: HTMLButtonElement | null = null;
  /**
   * Чекбокс-тумблер отладочного режима границ. Создаётся только при активной
   * админ-сессии (Требование 3.3); для неавторизованного пользователя остаётся
   * `null` (Требование 3.6).
   */
  private debugToggle: HTMLInputElement | null = null;

  // Обработчики указателя режима рисования (draw): привязываются к холсту.
  private readonly drawPointerDown = (e: PointerEvent): void => this.onDrawPointerDown(e);
  private readonly drawPointerMove = (e: PointerEvent): void => this.onDrawPointerMove(e);
  private readonly drawPointerUp = (e: PointerEvent): void => this.onDrawPointerUp(e);
  private drawHandlersAttached = false;

  // Обработчик клавиатуры отмены (Ctrl+Z/Cmd+Z) режима редактирования.
  private readonly keydownHandler = (e: KeyboardEvent): void => this.onKeyDown(e);
  private keydownAttached = false;

  // Управление циклом отрисовки.
  private renderScheduled = false;
  private disposed = false;
  private cssWidth = 1;
  private cssHeight = 1;
  private resizeObserver: ResizeObserver | null = null;
  private readonly windowResizeHandler = (): void => this.handleResize();

  /**
   * @param container контейнер страницы (в него добавляются холст, оверлей и панель)
   * @param mapId идентификатор карты для загрузки (`GET /api/maps/:id`)
   * @param options базовый URL API, функция загрузки и сторона панели легенды
   */
  constructor(container: HTMLElement, mapId: string, options: MapPageOptions = {}) {
    this.container = container;
    this.mapId = mapId;
    const fetchFn =
      options.fetchFn ??
      (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : (undefined as never));
    this.options = {
      apiBase: options.apiBase ?? '',
      fetchFn,
      legendPanelSide: options.legendPanelSide ?? 'right',
      onAuthChange: options.onAuthChange,
      onSaveStatus: options.onSaveStatus,
    };

    // Админ-сессия (Требование 7.1); хранилище/ключ внедряемы для тестов.
    this.session = new AdminSession({
      apiBase: this.options.apiBase,
      fetchFn,
      storage: options.authStorage,
      storageKey: options.authStorageKey,
    });

    this.webglAvailable = WebGLMapRenderer.isWebGLAvailable();
    if (!this.webglAvailable) {
      // Обработка ошибок: без WebGL карта не отображается (Требование 4.6/ошибки).
      renderWebGLUnavailableMessage(this.container);
      return;
    }
    this.buildDom();
  }

  /** Доступен ли WebGL в текущем окружении. */
  isWebGLAvailable(): boolean {
    return this.webglAvailable;
  }

  /**
   * Загрузить данные карты и собрать подсистемы.
   *
   * Выполняет `GET /api/maps/:id`, разбирает ответ ({@link parseMapResponse}),
   * восстанавливает фундаментальный многоугольник и склейку
   * (`computeSidePairing`), создаёт контроллер вида, LOD-менеджер, рендерер,
   * слои рисования и меток, боковую панель и запускает первую отрисовку.
   *
   * @throws Error при сетевой ошибке, ответе не-2xx или некорректных данных
   */
  async load(): Promise<MapData> {
    if (!this.webglAvailable || !this.canvas || !this.overlay) {
      throw new Error('WebGL недоступен: карта не может быть отображена');
    }

    const url = `${this.options.apiBase}/api/maps/${encodeURIComponent(this.mapId)}`;
    const response = await this.options.fetchFn(url);
    if (!response.ok) {
      throw new Error(
        response.status === 404
          ? 'Карта не найдена'
          : `Не удалось загрузить карту (код ${response.status})`,
      );
    }
    const json: unknown = await response.json();
    const data = parseMapResponse(json);
    this.data = data;
    this.assemble(data);
    this.requestRender();
    return data;
  }

  /**
   * Собрать подсистемы карты из разобранных данных. Вынесено из {@link load},
   * чтобы отделить сеть от сборки.
   */
  private assemble(data: MapData): void {
    const canvas = this.canvas as HTMLCanvasElement;
    const overlay = this.overlay as HTMLElement;
    const { config } = data;

    // Геометрия карты (Требования 6, 8): гиперболическая, евклидова или
    // сферическая — выбирает действие изометрий склейки, метрику подразбиения,
    // проекцию и навигацию. Единый источник для склейки, контроллера, LOD,
    // рендерера и слоёв рисунков/меток.
    const geometry = createGeometry(config.geometryType);
    this.geometry = geometry;

    // Соседние копии фундаментальной области (склейка → образующие → соседи).
    // Обобщённая склейка `computeSurfacePairing` строит изометрии через
    // `geometry.sidePairingIsometry` (без ограничения модели Пуанкаре, поэтому
    // не бросает исключение для евклидовых вершин на |z| = 1) и несёт признак
    // обращения ориентации для неориентируемых склеек (Требования 6, 8).
    const pairing: SurfacePairing = computeSurfacePairing(
      config.polygon,
      config.sidePairing,
      geometry,
    );

    // Контроллер вида: навигация pan/zoom, лимиты зума от масштаба карты.
    // Тип геометрии карты (`config.geometryType`) выбирает реализацию `Geometry`
    // для навигационной изометрии «захвата»: гиперболическая (по умолчанию),
    // евклидова или сферическая — сохраняющая модель с допуском 1e-6
    // (Требование 6.7). Множитель `config.maxZoomOut` задаёт предел отдаления
    // `minScale = 1 / (r · maxZoomOut)` (Требования 2.3, 2.5); он же
    // распространяется на LOD-менеджер ниже через `controller.getMinScale()`.
    const controller = new MapViewController(config.polygon, pairing, {
      mapScale: config.scale,
      maxZoomOut: config.maxZoomOut,
      // Админский предел отдаления (Требование 11): применяется, когда сессия
      // администратора подтверждена, через controller.setAdminZoom(true) из путей
      // авторизации (см. applyAdminZoom). До авторизации действует публичный
      // maxZoomOut (Требование 11.2).
      adminMaxZoomOut: config.adminMaxZoomOut,
      geometryType: config.geometryType,
      onChange: () => this.requestRender(),
      onScaleChange: (scale) => {
        this.renderer?.setScale(scale);
        this.requestRender();
      },
    });
    // Начальный масштаб — максимальное отдаление (видна ровно одна область).
    const initialScale = controller.setScaleClamped(controller.getMinScale());
    this.controller = controller;

    // LOD-менеджер: подбор уровня и отсечение по видимой области. Предел
    // отдаления передаётся как `minScale` из контроллера, где он уже учитывает
    // множитель `config.maxZoomOut` (`minScale = 1 / (r · maxZoomOut)`). Так
    // выбор уровня и потолок отдаления согласованы с навигацией (Требование 2.3).
    // Глубина прорисовки копий (`config.copyDepth`) задаёт глубину перечисления
    // соседних плиток `neighborDepth` (BFS по образующим фуксовой группы,
    // Требования 12.1, 12.2). Жёсткий потолок числа копий на кадр
    // `MAX_VISIBLE_COPIES` остаётся страховочным ограничением независимо от
    // `copyDepth` — он применяется через дефолт `maxNeighbors` и обход в
    // `LodManager.update` (Требование 12.5).
    // Режим обхода копий: при `vertexRingTraversal` LOD собирает полный цикл
    // копий вокруг каждой вершины области (вершинное кольцо) вместо BFS по длине
    // слова, что прорисовывает восьмиугольники с противоположной от текущей
    // области стороны вершины. По умолчанию — обход по глубине (историческое
    // поведение).
    this.lod = new LodManager(config.polygon, pairing, {
      mapScale: config.scale,
      minScale: controller.getMinScale(),
      neighborDepth: config.copyDepth,
      geometryType: config.geometryType,
      copyTraversal: config.vertexRingTraversal ? 'vertex' : 'depth',
    });

    // WebGL-рендерер: фон карты, начальный масштаб. Границы фундаментального
    // многоугольника по умолчанию ВЫКЛЮЧЕНЫ (Требования 3.1, 3.2, 3.4): вершины
    // границы загружаются, но проход отрисовки границ отключён. Включить их может
    // только авторизованный администратор тумблером отладочного режима
    // ({@link MapPage.setDebugBorders}).
    const renderer = new WebGLMapRenderer(canvas, {
      backgroundColor: config.backgroundColor,
      scale: initialScale,
      // Граница окружности Пуанкаре: скрывается, если в конфигурации карты
      // включён соответствующий флаг (иначе рисуется, как прежде).
      diskBoundary: { enabled: !config.hideDiskBoundary },
    });
    // Тип геометрии выбирает действие вершинного шейдера (SU(1,1) / аффинное /
    // SU(2) + проекция) и учёт антиголоморфного вида (Требования 6, 8).
    renderer.setGeometry(config.geometryType);
    renderer.setFundamentalDomain(config.polygon, { enabled: false });
    this.renderer = renderer;

    // Слой рисования: восстановить сохранённые примитивы.
    const drawingLayer = new DrawingLayer();
    drawingLayer.load(data.drawings);
    this.drawingLayer = drawingLayer;

    // Боковая панель легенды (Требование 5.2).
    this.legendPanel = new LegendPanel({ side: this.options.legendPanelSide });

    // Слой меток: клик открывает панель легенды (Требование 5).
    const markerLayer = new MarkerLayer(overlay, {
      project: (w, viewport, scale) => controller.diskToScreen(w, viewport, scale),
      onMarkerClick: (_marker, legendItem) => {
        if (legendItem) {
          this.legendPanel?.open(legendItem);
        }
      },
    });
    markerLayer.setLegend(data.legend);
    markerLayer.setMarkers(data.markers);
    this.markerLayer = markerLayer;

    // Подключить навигацию и обработчики меток к холсту.
    controller.attach(canvas);
    markerLayer.attach(canvas);

    // Синхронизировать размеры и подписаться на изменение размера контейнера.
    this.handleResize();
    this.observeResize();
  }

  /**
   * Цикл отрисовки кадра (Требования 4.6, 4.7, 5.1, 10.2, 10.4):
   * `LodManager.update → renderer.setTriangleMesh → renderer.render` (фон, меш,
   * граница диска), затем отрисовка рисунков, отладочный проход наложений
   * (`renderer.renderDebugOverlay` — поверх слоя рисования, чтобы маска
   * стирания их не перекрывала) и обновление позиций меток.
   */
  render(): void {
    if (this.disposed || !this.renderer || !this.controller || !this.lod) {
      return;
    }
    // Вид как обобщённая изометрия (матрица + признак обращения ориентации):
    // для неориентируемой поверхности после пересечения обращающей ориентацию
    // склейки вид антиголоморфен, и рендер даёт зеркальное продолжение
    // (Требование 8). Единый вид для меша, рисунков и меток.
    const view = this.controller.getViewIsometry();
    const geometry = this.geometry ?? undefined;
    const scale = this.controller.getScale();
    // Видимая область для отсечения — в пикселях буфера рисования холста.
    const canvas = this.canvas as HTMLCanvasElement;
    const backingViewport = { width: canvas.width, height: canvas.height };

    // 1. Подобрать уровень детализации и отсечь невидимые треугольники.
    const triangles = this.lod.update(view, scale, backingViewport);

    // 2. Отрисовать меш карты с фоновым цветом и границами.
    this.renderer.setView(view);
    this.renderer.setScale(scale);
    this.renderer.setTriangleMesh(triangles);
    this.renderer.render();

    // 3. Поверх меша — сохранённые примитивы рисования (в координатах поверхности).
    // Рисунки используют ТОТ ЖЕ перечень видимых копий, что и меш — кэш
    // `LodManager.getVisibleCopies()` из шага 1 (треугольники меша уже
    // построены под теми же γ). Меш и слой рисования делят единый конвейер
    // вид/масштаб/проекция (`V∘γ` в вершинном шейдере), поэтому одинаковые
    // координаты поверхности отображаются в одинаковые пиксели — рассогласование
    // на общих границах соседних копий удерживается ≤ 1 пикселя за счёт точных
    // изометрий склейки и их нормализации (Требования 4.4, 4.6).
    this.drawingLayer?.render(
      this.renderer,
      view,
      scale,
      this.lod.getVisibleCopyIsometries(),
      geometry,
    );

    // 4. Отладочные наложения (границы фундаментальной области и рёбра
    // треугольников) рисуются ПОСЛЕ слоя рисования (Требования 10.3, 10.4).
    // Иначе непрозрачная маска стирания ластика, наносимая слоем рисования
    // шагом 3, визуально перекрыла бы отладочные линии. Базовый
    // `renderer.render()` (шаг 2) рисует только фон, меш и границу диска.
    //
    // Буфер отладочных рёбер обновляется теми же треугольниками кадра, что и
    // основной меш (шаг 2), поэтому рёбра точно совпадают с отрисованной
    // триангуляцией во всех видимых копиях и перестраиваются каждый кадр вслед
    // за видом/масштабом/уровнем детализации (Требования 13.2, 13.5). Само
    // рисование рёбер внутри `renderDebugOverlay` включается только при активном
    // отладочном тумблере (Требование 13.3), поэтому кормить меш каждый кадр
    // дёшево и всегда корректно.
    this.renderer.setDebugTriangleMesh(triangles);
    this.renderer.renderDebugOverlay();

    // 5. Обновить позиции иконок меток (в CSS-пикселях оверлея).
    // Метки используют ТОТ ЖЕ перечень видимых копий, что и меш/рисунки —
    // `LodManager.getVisibleCopies()` (кэш последнего `update` из шага 1), а не
    // `controller.getNeighbors()`. Это гарантирует правило «метка рисуется в
    // копии тогда и только тогда, когда в ней рисуется контент» (Требование 5.4)
    // и включение вершинных соседей / исключение копий за пределом отдаления
    // (Требования 5.1, 5.2, 5.3). Вторичный пиксельный фильтр `isWithinViewport`
    // в `MarkerLayer` по-прежнему отсекает отдельные иконки вне кадра.
    const cssViewport = { width: this.cssWidth, height: this.cssHeight };
    this.markerLayer?.update(
      view,
      scale,
      cssViewport,
      this.lod.getVisibleCopyIsometries(),
      geometry,
    );
  }

  /**
   * Запланировать перерисовку на следующий кадр анимации (коалесцирование
   * множественных изменений вида/масштаба в один кадр).
   */
  private requestRender(): void {
    if (this.disposed || this.renderScheduled) {
      return;
    }
    this.renderScheduled = true;
    const raf =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback): number => {
            cb(0);
            return 0;
          };
    raf(() => {
      this.renderScheduled = false;
      this.render();
    });
  }

  // ---------------------------------------------------------------------------
  // Админ-сессия (Требования 7.1–7.5)
  // ---------------------------------------------------------------------------

  /** Подтверждена ли действующая админ-сессия. */
  isAuthenticated(): boolean {
    return this.session.isAuthenticated();
  }

  /**
   * Применить предел отдаления по признаку авторизации (Требование 11).
   *
   * Переключает админ-режим отдаления контроллера
   * ({@link MapViewController.setAdminZoom}): при авторизации действует
   * `adminMaxZoomOut`, иначе — публичный `maxZoomOut` (Требования 11.2, 11.3).
   * Пересчитанный `minScale` передаётся LOD-менеджеру
   * ({@link LodManager.setMinScale}) для согласования выбора уровня детализации,
   * после чего запрашивается перерисовка — предел обновляется без перезагрузки
   * карты, а текущий масштаб уже зажат контроллером в новый диапазон
   * (Требование 11.4).
   *
   * Привязано именно к признаку авторизации (вызывается из
   * {@link checkAdminSession}/{@link login}/{@link logout}), а не к открытию
   * панели редактирования. Безопасно вызывается до загрузки карты (контроллер
   * ещё не создан — no-op).
   *
   * @param enabled `true`, когда сессия администратора подтверждена
   */
  private applyAdminZoom(enabled: boolean): void {
    if (!this.controller) {
      return;
    }
    this.controller.setAdminZoom(enabled);
    this.lod?.setMinScale(this.controller.getMinScale());
    this.requestRender();
  }

  /**
   * Проверить наличие действующей админ-сессии пробным запросом к защищённому
   * эндпоинту (как в `admin.html`) и, при наличии, включить режим редактирования
   * (Требования 7.1, 7.4). Уведомляет через `onAuthChange`.
   *
   * @returns признак наличия действующей сессии
   */
  async checkAdminSession(): Promise<boolean> {
    const ok = await this.session.probe();
    if (ok) {
      this.enableEditing();
    } else {
      this.disableEditing();
    }
    // Предел отдаления зависит от авторизации, а не от режима редактирования
    // (Требование 11): применяем его по результату проверки сессии.
    this.applyAdminZoom(ok);
    this.options.onAuthChange?.(ok);
    return ok;
  }

  /**
   * Войти под учётными данными администратора (Требование 7.1). При успехе
   * включает режим редактирования и уведомляет через `onAuthChange`.
   *
   * @param username логин администратора
   * @param password пароль администратора
   * @returns `true`, если вход успешен
   */
  async login(username: string, password: string): Promise<boolean> {
    const ok = await this.session.login(username, password);
    if (ok) {
      this.enableEditing();
      // Вход подтверждён — применить админский предел отдаления (Требование 11.3).
      this.applyAdminZoom(true);
    }
    this.options.onAuthChange?.(ok);
    return ok;
  }

  /**
   * Завершить админ-сессию (Требование 7.3): очистить учётные данные, скрыть все
   * инструменты редактирования и вернуть карту в режим только просмотра
   * (Требование 7.4). Уведомляет через `onAuthChange`.
   */
  logout(): void {
    this.session.logout();
    this.disableEditing();
    // Выход — вернуть публичный предел отдаления (Требование 11.2, 11.4).
    this.applyAdminZoom(false);
    this.options.onAuthChange?.(false);
  }

  // ---------------------------------------------------------------------------
  // Режим редактирования (Требования 7.1, 8, 9, 10.1)
  // ---------------------------------------------------------------------------

  /**
   * Включить режим редактирования: панель инструментов рисования
   * ({@link DrawingTools}), размещение меток ({@link MarkerEditor}) и кнопку
   * сохранения (Требования 7.1, 8, 9, 10.1). Идемпотентен. Требует загруженной
   * карты и доступного WebGL; иначе возвращает `false`.
   *
   * @returns `true`, если режим редактирования включён
   */
  enableEditing(): boolean {
    if (this.editing) {
      return true;
    }
    if (
      !this.canvas ||
      !this.controller ||
      !this.drawingLayer ||
      !this.markerLayer ||
      !this.data
    ) {
      return false;
    }
    this.editing = true;

    // История отмены (Ctrl+Z, Требование 10): снимок слоя проталкивается перед
    // каждым завершённым действием, чтобы отмена восстанавливала состояние до
    // него (Требования 10.1–10.4).
    this.drawingHistory = new DrawingHistory();
    this.lastMarkerCount = this.data.markers.length;

    // Инструменты рисования: построенные примитивы кладутся в слой и сразу
    // отрисовываются (Требование 8), изменения помечаются несохранёнными.
    this.drawingTools = new DrawingTools({
      layer: this.drawingLayer,
      controller: this.controller,
      getFillLevel: () => this.lod?.getLevel() ?? 0,
      onPrimitive: (primitive) => {
        // Снимок состояния слоя ДО применения примитива — один отменяемый шаг
        // (Требования 10.1, 10.2).
        this.drawingHistory?.push(this.drawingLayer?.serialize() ?? []);
        this.drawingLayer?.add(primitive);
        this.markDirty();
        this.requestRender();
      },
    });

    // Редактор меток: размещение/перемещение/удаление меток в режиме рисования
    // (Требование 9); изменения немедленно отражаются в слое меток.
    this.markerEditor = new MarkerEditor({
      mapId: this.data.config.id,
      layer: this.drawingLayer,
      controller: this.controller,
      markerLayer: this.markerLayer,
      initialMarkers: this.data.markers,
      selectedLegendItemId: this.data.legend[0]?.id ?? null,
      onChange: (markers) => {
        this.markerLayer?.setMarkers(markers);
        this.requestRender();
      },
      onMarkersChanged: (markers) => {
        // Установка новой метки — отдельный отменяемый шаг (Требование 10.1):
        // проталкиваем снимок слоя, когда число меток выросло. Перемещение и
        // удаление (число меток не растёт) историю не засоряют.
        if (markers.length > this.lastMarkerCount) {
          this.drawingHistory?.push(this.drawingLayer?.serialize() ?? []);
        }
        this.lastMarkerCount = markers.length;
        this.markDirty();
      },
    });

    this.buildEditingPanel();
    // Обработчик отмены Ctrl+Z/Cmd+Z действует в течение всего режима
    // редактирования (Требования 10.2, 10.5–10.7).
    this.attachKeyboardHandler();
    // Начинаем с навигации: инструменты видны, но перетаскивание пока панорамирует.
    this.setEditingMode('navigate');
    return true;
  }

  /**
   * Выключить режим редактирования и вернуть карту к режиму только просмотра
   * (Требование 7.4): снять обработчики рисования/меток, удалить панель
   * инструментов и восстановить навигацию/клики по меткам. Идемпотентен.
   */
  disableEditing(): void {
    if (!this.editing) {
      return;
    }
    this.editing = false;

    this.detachDrawHandlers();
    this.detachKeyboardHandler();
    this.drawingHistory = null;
    this.lastMarkerCount = 0;
    this.markerEditor?.detach();
    this.markerEditor = null;
    this.drawingTools?.dispose();
    this.drawingTools = null;

    this.editingPanel?.remove();
    this.editingPanel = null;
    this.modeButtons.clear();
    this.legendSelect = null;
    this.saveButton = null;
    this.debugToggle = null;
    this.editingMode = 'navigate';

    // Отладочные границы — админ-функция; при выходе из режима редактирования
    // (в т.ч. logout) вернуть их в выключенное состояние, чтобы для
    // неавторизованного пользователя границы не отрисовывались (Требования 3.1,
    // 3.6).
    if (this.debugBorders) {
      this.setDebugBorders(false);
    }

    // Восстановить взаимодействие только для просмотра (навигация + клики меток).
    if (this.canvas) {
      this.controller?.detach();
      this.markerLayer?.detach();
      this.controller?.attach(this.canvas);
      this.markerLayer?.attach(this.canvas);
    }
  }

  /** Находится ли страница в режиме редактирования. */
  isEditing(): boolean {
    return this.editing;
  }

  /** Текущий режим взаимодействия в режиме редактирования. */
  getEditingMode(): EditingMode {
    return this.editingMode;
  }

  /**
   * Переключить режим взаимодействия в режиме редактирования. Только один
   * «владелец» указателя активен одновременно: навигация (pan/zoom), рисование
   * или размещение меток — это исключает конфликты обработчиков холста.
   *
   * @param mode целевой режим взаимодействия
   */
  setEditingMode(mode: EditingMode): void {
    if (!this.editing || !this.canvas) {
      return;
    }
    this.editingMode = mode;
    const canvas = this.canvas;

    // Снять всех текущих владельцев указателя.
    this.controller?.detach();
    this.markerEditor?.detach();
    this.markerLayer?.detach();
    this.detachDrawHandlers();

    if (mode === 'navigate') {
      this.controller?.attach(canvas);
      this.markerLayer?.attach(canvas);
    } else if (mode === 'draw') {
      this.attachDrawHandlers(canvas);
    } else {
      this.markerEditor?.attach(canvas);
    }
    this.syncModeButtons();
  }

  /**
   * Включить/выключить отладочный режим (Требования 3, 13). Единый клиентский
   * тумблер {@link MapPage.debugBorders} управляет ОБОИМИ отладочными
   * наложениями: границами фундаментального многоугольника
   * (`setFundamentalDomain(polygon, { enabled })`, Требование 3) и рёбрами
   * треугольников триангуляции (`setTriangleDebug({ enabled })`, Требования 13.1,
   * 13.3). Оба прохода выполняются в {@link WebGLMapRenderer.renderDebugOverlay}
   * поверх слоя рисования; треугольники обновляются каждый кадр в
   * {@link MapPage.render} через `setDebugTriangleMesh` (Требование 13.5).
   *
   * После обновления состояния запрашивается перерисовка на ближайшем кадре
   * анимации, что укладывается в 1 секунду (Требования 3.5, 3.7). Остальное
   * содержимое карты (меш, рисунки, метки) не затрагивается — отладочные линии
   * рисуются отдельным проходом поверх меша.
   *
   * @param value `true`, чтобы отрисовывать отладочные наложения; `false` — скрыть
   */
  setDebugBorders(value: boolean): void {
    this.debugBorders = value;
    const polygon = this.data?.config.polygon;
    if (this.renderer && polygon) {
      this.renderer.setFundamentalDomain(polygon, { enabled: value });
    }
    // Тот же тумблер управляет отладочными рёбрами треугольников (Требования
    // 13.1, 13.3): при выключении (в т.ч. сброс в disableEditing/logout) проход
    // рёбер также отключается (Требование 13.4).
    this.renderer?.setTriangleDebug({ enabled: value });
    if (this.debugToggle && this.debugToggle.checked !== value) {
      this.debugToggle.checked = value;
    }
    this.requestRender();
  }

  /** Включено ли отладочное отображение границ (Требование 3). */
  isDebugBorders(): boolean {
    return this.debugBorders;
  }

  /**
   * Сохранить рисунки и метки текущей карты через `PUT` эндпоинты с заголовком
   * авторизации (Требования 10.1, 10.4). Запросы без валидной сессии отклоняются
   * сервером (Требование 7.5). Уведомляет о статусе через `onSaveStatus`.
   *
   * Перед сохранением снимается снимок слоя рисования (`serialize()`). Если
   * `PUT /drawings` не удался (в т.ч. при сохранении результата стирания
   * ластиком), слой восстанавливается из снимка (`load` + `requestRender`),
   * чтобы удалённый/нанесённый контент не пропал визуально, и показывается
   * сообщение об ошибке через `onSaveStatus` (Требование 1.7).
   *
   * @returns `true`, если оба запроса завершились успешно
   */
  async save(): Promise<boolean> {
    if (!this.editing || !this.session.isAuthenticated() || !this.data) {
      return false;
    }
    this.options.onSaveStatus?.('saving');

    // Снимок содержимого слоя рисования ДО сохранения (Требование 1.7): служит
    // точкой отката при неуспешном `PUT /drawings`.
    const drawingSnapshot: DrawingPrimitive[] = this.drawingLayer?.serialize() ?? [];

    try {
      await this.saveDrawings();
    } catch (error) {
      // Откат слоя к состоянию до операции стирания/рисования и перерисовка,
      // затем сообщение об ошибке через существующий механизм (Требование 1.7).
      this.rollbackDrawings(drawingSnapshot);
      this.options.onSaveStatus?.('error');
      return false;
    }

    try {
      await this.saveMarkers();
    } catch (error) {
      // Рисунки уже сохранены на сервере успешно, поэтому слой не откатывается —
      // локальное состояние согласовано с сервером; сообщаем об ошибке меток.
      this.options.onSaveStatus?.('error');
      return false;
    }

    this.dirty = false;
    this.updateSaveButton();
    this.options.onSaveStatus?.('saved');
    return true;
  }

  /**
   * Восстановить содержимое слоя рисования из снимка, снятого до сохранения, и
   * запросить перерисовку (Требование 1.7). Вызывается при неуспешном
   * `PUT /drawings` (в частности, при сохранении результата стирания ластиком),
   * чтобы стёртый или нанесённый контент не пропал визуально после неудачного
   * сохранения. Восстановление синхронно (`DrawingLayer.load`), перерисовка идёт
   * на ближайшем кадре анимации через {@link MapPage.requestRender}.
   *
   * @param snapshot сериализованное состояние слоя до операции сохранения
   */
  private rollbackDrawings(snapshot: DrawingPrimitive[]): void {
    this.drawingLayer?.load(snapshot);
    this.requestRender();
  }

  /** Отправить рисунки карты на сервер (`PUT /drawings`). */
  private async saveDrawings(): Promise<void> {
    const id = this.data?.config.id;
    if (!id || !this.drawingLayer) {
      return;
    }
    const url = `${this.options.apiBase}/api/mapsadmin/maps/${encodeURIComponent(id)}/drawings`;
    const response = await this.options.fetchFn(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...this.session.authHeaders() },
      body: JSON.stringify(this.drawingLayer.serialize()),
    });
    if (!response.ok) {
      throw new Error(`Не удалось сохранить рисунки (код ${response.status})`);
    }
  }

  /** Отправить метки карты на сервер (`PUT /markers`). */
  private async saveMarkers(): Promise<void> {
    const id = this.data?.config.id;
    if (!id || !this.markerEditor) {
      return;
    }
    // Сервер ожидает координаты поверхности и ссылку на элемент легенды;
    // идентификаторы генерируются в БД (Требование 9.2).
    const payload = this.markerEditor.getMarkers().map((m) => ({
      legendItemId: m.legendItemId,
      surface: m.surface,
    }));
    const url = `${this.options.apiBase}/api/mapsadmin/maps/${encodeURIComponent(id)}/markers`;
    const response = await this.options.fetchFn(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...this.session.authHeaders() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`Не удалось сохранить метки (код ${response.status})`);
    }
  }

  /** Пометить контент как несохранённый и обновить кнопку сохранения. */
  private markDirty(): void {
    this.dirty = true;
    this.updateSaveButton();
  }

  // ---------------------------------------------------------------------------
  // Панель инструментов редактирования (тонкий DOM)
  // ---------------------------------------------------------------------------

  /**
   * Построить панель режима редактирования: переключатель режимов
   * (навигация/рисование/метки), панель инструментов рисования, выбор элемента
   * легенды для меток и кнопку сохранения. Безопасно вырождается вне браузера.
   */
  private buildEditingPanel(): void {
    if (typeof document === 'undefined' || !this.data) {
      return;
    }
    const panel = document.createElement('div');
    panel.className = 'map-editing-panel';

    // Переключатель режимов взаимодействия.
    const modes = document.createElement('div');
    modes.className = 'map-editing-panel__modes';
    const modeDefs: Array<[EditingMode, string]> = [
      ['navigate', 'Навигация'],
      ['draw', 'Рисование'],
      ['markers', 'Метки'],
    ];
    for (const [mode, label] of modeDefs) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'map-editing-panel__mode';
      button.textContent = label;
      button.addEventListener('click', () => this.setEditingMode(mode));
      this.modeButtons.set(mode, button);
      modes.appendChild(button);
    }
    panel.appendChild(modes);

    // Панель инструментов рисования (кисть/фигуры/заливка/цвета).
    const toolbar = this.drawingTools?.createToolbar();
    if (toolbar) {
      panel.appendChild(toolbar);
    }

    // Выбор элемента легенды для размещения меток (Требование 9.1).
    const legendRow = document.createElement('div');
    legendRow.className = 'map-editing-panel__legend';
    const legendLabel = document.createElement('label');
    legendLabel.textContent = 'Метка:';
    const select = document.createElement('select');
    if (this.data.legend.length === 0) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = 'Нет элементов легенды';
      select.appendChild(option);
      select.disabled = true;
    } else {
      for (const item of this.data.legend) {
        const option = document.createElement('option');
        option.value = item.id;
        option.textContent = item.name;
        select.appendChild(option);
      }
    }
    select.addEventListener('change', () => {
      this.markerEditor?.selectLegendItem(select.value || null);
    });
    legendLabel.appendChild(select);
    legendRow.appendChild(legendLabel);
    panel.appendChild(legendRow);
    this.legendSelect = select;

    // Тумблер отладочного отображения границ фундаментального многоугольника
    // (Требование 3). Создаётся ТОЛЬКО при активной админ-сессии (Требование
    // 3.3); для неавторизованного пользователя тумблер не добавляется вовсе
    // (Требование 3.6). Начальное состояние — выключено (Требование 3.4).
    if (this.session.isAuthenticated()) {
      const debugRow = document.createElement('div');
      debugRow.className = 'map-editing-panel__debug';
      const debugLabel = document.createElement('label');
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'map-editing-panel__debug-toggle';
      checkbox.checked = this.debugBorders;
      checkbox.addEventListener('change', () => {
        this.setDebugBorders(checkbox.checked);
      });
      debugLabel.appendChild(checkbox);
      debugLabel.appendChild(document.createTextNode(' Границы (отладка)'));
      debugRow.appendChild(debugLabel);
      panel.appendChild(debugRow);
      this.debugToggle = checkbox;
    }

    // Кнопка сохранения (Требование 10.1).
    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'map-editing-panel__save';
    save.textContent = 'Сохранить';
    save.addEventListener('click', () => {
      void this.save();
    });
    panel.appendChild(save);
    this.saveButton = save;

    this.container.appendChild(panel);
    this.editingPanel = panel;
    this.updateSaveButton();
  }

  /** Отразить активный режим в кнопках переключателя. */
  private syncModeButtons(): void {
    for (const [mode, button] of this.modeButtons) {
      button.classList.toggle(
        'map-editing-panel__mode--active',
        mode === this.editingMode,
      );
    }
  }

  /** Обновить подпись/состояние кнопки сохранения по флагу несохранённого. */
  private updateSaveButton(): void {
    if (!this.saveButton) {
      return;
    }
    this.saveButton.textContent = this.dirty ? 'Сохранить *' : 'Сохранить';
  }

  // ---------------------------------------------------------------------------
  // Обработчики указателя режима рисования
  // ---------------------------------------------------------------------------

  /** Привязать обработчики указателя рисования к холсту. */
  private attachDrawHandlers(canvas: HTMLCanvasElement): void {
    if (typeof window === 'undefined' || this.drawHandlersAttached) {
      return;
    }
    canvas.addEventListener('pointerdown', this.drawPointerDown);
    canvas.addEventListener('pointermove', this.drawPointerMove);
    window.addEventListener('pointerup', this.drawPointerUp);
    this.drawHandlersAttached = true;
  }

  /** Снять обработчики указателя рисования и отменить незавершённый штрих. */
  private detachDrawHandlers(): void {
    if (!this.drawHandlersAttached) {
      return;
    }
    if (this.canvas) {
      this.canvas.removeEventListener('pointerdown', this.drawPointerDown);
      this.canvas.removeEventListener('pointermove', this.drawPointerMove);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerup', this.drawPointerUp);
    }
    this.drawingTools?.cancel();
    this.drawHandlersAttached = false;
  }

  /** Подключить обработчик клавиатуры отмены (Ctrl+Z/Cmd+Z) к окну. */
  private attachKeyboardHandler(): void {
    if (typeof window === 'undefined' || this.keydownAttached) {
      return;
    }
    window.addEventListener('keydown', this.keydownHandler);
    this.keydownAttached = true;
  }

  /** Снять обработчик клавиатуры отмены. */
  private detachKeyboardHandler(): void {
    if (!this.keydownAttached) {
      return;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keydownHandler);
    }
    this.keydownAttached = false;
  }

  /**
   * Обработчик клавиатуры режима редактирования (Требование 10.2): сочетание
   * Ctrl+Z (Windows/Linux) или Cmd+Z (macOS) отменяет последнее действие
   * рисования. Комбинации с Shift/Alt (например, redo) игнорируются. Отмена
   * выполняется только при активном режиме редактирования.
   */
  private onKeyDown(e: KeyboardEvent): void {
    if (!this.editing) {
      return;
    }
    const isUndoCombo =
      (e.ctrlKey || e.metaKey) &&
      !e.altKey &&
      !e.shiftKey &&
      (e.key === 'z' || e.key === 'Z');
    if (!isUndoCombo) {
      return;
    }
    // Отмена регистрируется только при непустой истории; иначе — no-op и
    // событие не перехватывается (Требование 10.5).
    if (this.undo() && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
  }

  /**
   * Отменить последнее сохранённое действие рисования (Ctrl+Z, Требование 10.2):
   * снять последний снимок истории и восстановить состояние слоя, существовавшее
   * непосредственно до действия (`DrawingLayer.load`), затем перерисовать карту
   * (Требование 10.6 — восстановление и перерисовка в пределах 200 мс за счёт
   * синхронного `load` и `requestRender` на ближайшем кадре). При пустой истории
   * содержимое слоя не меняется и операция отмены не регистрируется (Требование
   * 10.5). Повторные вызовы отменяют шаги в порядке LIFO, пока история не
   * опустеет (Требование 10.7).
   *
   * @returns `true`, если шаг был отменён; `false` при пустой истории
   */
  undo(): boolean {
    if (
      !this.drawingHistory ||
      !this.drawingLayer ||
      !this.drawingHistory.canUndo()
    ) {
      return false;
    }
    const previous = this.drawingHistory.undo();
    if (!previous) {
      return false;
    }
    this.drawingLayer.load(previous);
    this.markDirty();
    this.requestRender();
    return true;
  }

  /** Размеры холста в пикселях буфера рисования (совместимо с проекцией вида). */
  private backingViewport(): Viewport {
    const canvas = this.canvas as HTMLCanvasElement;
    return {
      width: canvas.width || canvas.clientWidth || 1,
      height: canvas.height || canvas.clientHeight || 1,
    };
  }

  /** Пиксель события указателя в координатах буфера рисования холста. */
  private backingPixel(e: MouseEvent): { x: number; y: number } {
    const canvas = this.canvas as HTMLCanvasElement;
    if (typeof canvas.getBoundingClientRect === 'function') {
      const rect = canvas.getBoundingClientRect();
      const scaleX = (canvas.width || rect.width) / (rect.width || 1);
      const scaleY = (canvas.height || rect.height) / (rect.height || 1);
      return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    }
    return { x: e.clientX, y: e.clientY };
  }

  /** Перевести пиксель события в точку поверхности через инструменты рисования. */
  private drawSurfacePoint(e: MouseEvent): Complex | null {
    if (!this.drawingTools) {
      return null;
    }
    const pixel = this.backingPixel(e);
    return this.drawingTools.screenToSurface(pixel.x, pixel.y, this.backingViewport());
  }

  /** Начало штриха/фигуры/заливки в режиме рисования. */
  private onDrawPointerDown(e: PointerEvent): void {
    if (!this.drawingTools) {
      return;
    }
    const surface = this.drawSurfacePoint(e);
    if (!surface) {
      return;
    }
    this.drawingTools.begin(surface);
    const canvas = this.canvas as HTMLCanvasElement & {
      setPointerCapture?: (id: number) => void;
    };
    if (typeof canvas.setPointerCapture === 'function') {
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // Захват указателя не критичен.
      }
    }
  }

  /** Продолжение штриха/фигуры при перетаскивании. */
  private onDrawPointerMove(e: PointerEvent): void {
    if (!this.drawingTools || !this.drawingTools.isDrawing()) {
      return;
    }
    const surface = this.drawSurfacePoint(e);
    if (surface) {
      this.drawingTools.update(surface);
    }
  }

  /** Завершение штриха/фигуры/заливки (примитив добавляется через onPrimitive). */
  private onDrawPointerUp(e: PointerEvent): void {
    if (!this.drawingTools || !this.drawingTools.isDrawing()) {
      return;
    }
    const surface = this.drawSurfacePoint(e);
    this.drawingTools.end(surface ?? undefined);
    const canvas = this.canvas as HTMLCanvasElement & {
      releasePointerCapture?: (id: number) => void;
    };
    if (typeof canvas.releasePointerCapture === 'function') {
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // Игнорируем: указатель мог не быть захвачен.
      }
    }
  }

  /** Разобранные данные загруженной карты (или `null` до загрузки). */
  getData(): MapData | null {
    return this.data;
  }

  /** Создать DOM-структуру: холст карты и оверлей меток внутри контейнера. */
  private buildDom(): void {
    if (typeof document === 'undefined') {
      return;
    }
    this.container.textContent = '';
    const styleTarget = this.container.style;
    if (styleTarget) {
      styleTarget.position = styleTarget.position || 'relative';
      styleTarget.overflow = 'hidden';
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'map-canvas';
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.touchAction = 'none';
    this.container.appendChild(canvas);
    this.canvas = canvas;

    const overlay = document.createElement('div');
    overlay.className = 'map-marker-overlay';
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.pointerEvents = 'none';
    this.container.appendChild(overlay);
    this.overlay = overlay;
  }

  /** Подписаться на изменение размеров контейнера (ResizeObserver + window resize). */
  private observeResize(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.container);
    }
    window.addEventListener('resize', this.windowResizeHandler);
  }

  /**
   * Синхронизировать размер буфера рисования холста с CSS-размером контейнера с
   * учётом `devicePixelRatio` и перерисовать. Метки позиционируются в CSS-пикселях.
   */
  private handleResize(): void {
    if (!this.canvas || !this.renderer) {
      return;
    }
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const cssWidth = Math.max(1, this.container.clientWidth || this.cssWidth);
    const cssHeight = Math.max(1, this.container.clientHeight || this.cssHeight);
    this.cssWidth = cssWidth;
    this.cssHeight = cssHeight;
    this.renderer.resize(Math.round(cssWidth * dpr), Math.round(cssHeight * dpr));
    this.requestRender();
  }

  /** Освободить ресурсы: обработчики, наблюдатели, WebGL и DOM-панель. */
  dispose(): void {
    this.disposed = true;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.windowResizeHandler);
    }
    // Снять инструменты редактирования (обработчики рисования/меток, панель).
    this.detachDrawHandlers();
    this.detachKeyboardHandler();
    this.drawingHistory = null;
    this.markerEditor?.dispose();
    this.markerEditor = null;
    this.drawingTools?.dispose();
    this.drawingTools = null;
    this.editingPanel?.remove();
    this.editingPanel = null;
    this.controller?.detach();
    this.markerLayer?.dispose();
    this.legendPanel?.dispose();
    this.renderer?.dispose();
    this.controller = null;
    this.lod = null;
    this.drawingLayer = null;
    this.markerLayer = null;
    this.legendPanel = null;
    this.renderer = null;
  }
}
